import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { hasFreeAnonymousHints, getHintAuthMessage } from '../utils/anonymousHints';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { buildTrackedAuthRedirectUrl, logGameEvent, trackGameHintInsufficientBalance } from "../analytics";
import { getGameConfig, getGameMessage } from "../games";
import { GameShell, useAiutiniModal } from "./layout";
import { GameEndSection } from "./game";
import SnackAlert from "./SnackAlert.js";
import { OrtografiaSkeleton } from "./Skeleton";
import { hasGameHistory, getMostRecentDifficulty } from "../util";
import styles from "../styles/Ortografia.module.css";
import "../styles/Instructions.css";
import modalStyles from "../styles/modal.module.css";
import gameStyles from "../styles/GameStyles.module.css";
import Button, { IconButton } from "./Button";
import { useGettoncini } from "../contexts/GettonciniContext";
import { useStellineRewards } from "../contexts/StellineRewardsContext";
import useHasEverAuthenticated from "../hooks/useHasEverAuthenticated";
import { useOrtografiaEvents } from "../hooks/games/useOrtografiaEvents";
import { GameStatus } from "../types/gameplay";
import { useAIHelper } from "../contexts/AIHelperContext";
import { usePuzzleInstances } from "../contexts/PuzzleInstancesContext";
import DifficultySelectionContent from "./OrtografiaDifficultySelectionContent";
import GettonciniCostChip from "./GettonciniCostChip";
import RewardedGettonciniCard from './RewardedGettonciniCard';
import FaIcon from './FaIcon';
import { getOrtografiaInvalidReasonMessage } from "../utils/ortografiaSubmit";
import { getDisabledHintCount, getDisabledHintsMessage } from "../utils/aiutiniModal";
// import AIHelperTrigger from './ai-helper/AIHelperTrigger'; // AI trigger disabled for now

const INSUFFICIENT_HINT_BALANCE_MESSAGE = "Gettoncini insufficienti per questo aiutino.";

const canAffordHintCost = ({ cost, balance, isPreview = false, isFree = false }) => {
  if (isPreview || isFree) return true;
  if (typeof cost !== "number" || typeof balance !== "number") return true;
  return balance >= cost;
};

const removeAccents = (str) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

const OrtografiaGame = ({
  date,
  gameConfig: providedGameConfig,
  puzzleConfig,
  sessionState,
  solutions,
  solutionsAvailable = false,
  status,
  won,
  justWon,
  justLost = false,
  isLoading,
  sessionError,
  isEventPending,
  dispatchEvent,
  dispatchHintEvent,
  hintAllowance = null,
  refreshHintAllowance = null,
  isSignedIn = true,
  isPreview = false,
  previewSponsorship = null,
  showNavigation = true,
  showGameHeader = true,
  previewEndLabel,
}) => {
  const hasFreeHints = hasFreeAnonymousHints(hintAllowance, 'ortografia', isSignedIn);
  const navigate = useNavigate();
  const location = useLocation();
  const hasEverAuthenticated = useHasEverAuthenticated();

  const gameConfig = providedGameConfig || getGameConfig("ortografia");
  const effectiveDate = date || "preview";
  const shouldTrack = !isPreview;
  const { getPuzzleInstance } = usePuzzleInstances();
  const sponsorship = previewSponsorship || (isPreview ? null : getPuzzleInstance(gameConfig.id, effectiveDate)?.sponsorship || null);

  // Get puzzle instance ID for AI helper (disabled for now)
  // const { getPuzzleInstanceId } = usePuzzleInstances();
  // const puzzleInstanceId = getPuzzleInstanceId(gameConfig.id, effectiveDate);

  // AI helper — Ortografia shows a static message instead of the full AI flow
  const { setSelection: setAISelection } = useAIHelper();
  const handleWordClick = useCallback((word) => {
    setAISelection({
      text: word,
      selection: { component: 'found_word', word },
      staticResponse: "Le parole sono come le persone: non sono mai isole, appartengono sempre ad una comunità e ad un contesto. Pertanto, purtroppo, in questo momento **non posso aiutarti con i contenuti di questo gioco**.",
    });
  }, [setAISelection]);

  // Ortografia-specific event helpers
  const { setDifficulty, submitWord, requestScaffold, viewSolutions } = useOrtografiaEvents(dispatchEvent, dispatchHintEvent);

  // Get gettoncini context for hint costs
  const { balance, getHintCost } = useGettoncini();
  const { getOrtografiaDifficultyReward } = useStellineRewards();

  // Local UI state
  const [currentWord, setCurrentWord] = useState("");
  const [shuffledLetters, setShuffledLetters] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDifficultySelection, setShowDifficultySelection] = useState(false);
  const [pendingDifficulty, setPendingDifficulty] = useState(() => {
    // Determine the default difficulty based on previous play history
    let defaultDifficulty = "A2";
    if (hasGameHistory(gameConfig.id)) {
      const mostRecentDifficulty = getMostRecentDifficulty(gameConfig.id);
      if (mostRecentDifficulty) {
        defaultDifficulty = mostRecentDifficulty;
      }
    }
    return defaultDifficulty;
  });
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
  const [previousLevelIndex, setPreviousLevelIndex] = useState(0); // Track for fill animation
  const [levelUpAnimation, setLevelUpAnimation] = useState(false); // Triggers level-up animation sequence
  const [scaffoldDismissed, setScaffoldDismissed] = useState(false); // Local flag to temporarily dismiss scaffold UI
  const [loadingHint, setLoadingHint] = useState(null);
  const [aiutiniError, setAiutiniError] = useState("");
  const [endSectionAnchor, setEndSectionAnchor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmitPending = isSubmitting || (isEventPending && loadingHint === null);
  const [continuingAfterWin, setContinuingAfterWin] = useState(false); // Allows player to keep playing after winning

  // Track milestone so we don't show it multiple times
  const prevScoreRef = useRef(0);
  const hasShownInstructionsRef = useRef(false);
  const hasCheckedInitialDifficultyRef = useRef(false);

  const snackAlertRef = useRef();
  const gameplayControlsRef = useRef(null);

  // Store handler ref to avoid infinite loops in useEffect
  const handleScaffoldRef = useRef();
  const aiutiniModalSignatureRef = useRef("");

  // Modal state
  const {
    isModalOpen,
    modalTitle,
    modalContent,
    closeModal: closeAnyModal,
    showModal,
    showAiutiniModal,
    refreshAiutiniModal,
    isAiutiniModalOpen,
  } = useAiutiniModal();

  useEffect(() => {
    if (isAiutiniModalOpen) refreshHintAllowance?.();
  }, [isAiutiniModalOpen, refreshHintAllowance]);

  const [isDifficultyModalOpen, setIsDifficultyModalOpen] = useState(false);
  const [pendingDifficultyModal, setPendingDifficultyModal] = useState(false);
  const closeModal = useCallback(() => {
    setIsDifficultyModalOpen(false);
    closeAnyModal();
  }, [closeAnyModal]);

  // Derived state from session
  const score = sessionState?.score ?? 0;
  const submittedWords = sessionState?.submitted_words ?? [];
  const difficulty = sessionState?.difficulty ?? pendingDifficulty;
  const seq = sessionState?.seq ?? 0;

  // Scaffold from session state (can be dismissed locally)
  const sessionScaffold = sessionState?.scaffold;

  // Reset dismissed flag when scaffold changes (e.g., new scaffold requested or cleared after word submission)
  const prevSessionScaffoldRef = useRef(sessionScaffold);
  useEffect(() => {
    // If scaffold changed (new scaffold or cleared), reset dismissed flag
    if (sessionScaffold !== prevSessionScaffoldRef.current) {
      setScaffoldDismissed(false);
      prevSessionScaffoldRef.current = sessionScaffold;
    }
  }, [sessionScaffold]);

  const scaffold = useMemo(() => {
    if (scaffoldDismissed || !sessionScaffold) return null;
    // Convert revealed_letters keys from strings to integers
    const revealedLetters = {};
    if (sessionScaffold.revealed_letters) {
      for (const [key, value] of Object.entries(sessionScaffold.revealed_letters)) {
        revealedLetters[parseInt(key, 10)] = value;
      }
    }
    return {
      wordLength: sessionScaffold.word_length,
      scaffold: sessionScaffold.scaffold,
      revealedPositions: sessionScaffold.revealed_positions,
      revealedLetters,
    };
  }, [sessionScaffold, scaffoldDismissed]);

  // Derived from puzzleConfig
  const letters = useMemo(() => {
    if (!puzzleConfig) return [];
    return (puzzleConfig.letters || []).filter((l) => l !== puzzleConfig.center);
  }, [puzzleConfig]);
  const centerLetter = puzzleConfig?.center ?? "";
  const levels = puzzleConfig?.levels ?? {};
  const hints = puzzleConfig?.hints ?? null;

  // Check if player has reached their originally selected difficulty level
  // (moved early so it can be used in keyboard effect)
  const originalLevelEntries = useMemo(() => {
    return Object.entries(levels)
      .filter(([level]) => level <= difficulty)
      .sort((x, y) => x[1] - y[1]);
  }, [levels, difficulty]);

  let hasReachedOriginalDifficulty = false;
  if (originalLevelEntries.length > 0) {
    const maxOriginalLevel = originalLevelEntries[originalLevelEntries.length - 1];
    hasReachedOriginalDifficulty = score >= maxOriginalLevel[1];
  }

  const hasSolutions = Array.isArray(solutions) && solutions.length > 0;
  const willViewingSolutionsForfeitGame =
    !hasSolutions && status !== GameStatus.WON && status !== GameStatus.LOST;

  // Show win state only for actually won sessions and only if not continuing to play
  const showWinState = status === GameStatus.WON && hasReachedOriginalDifficulty && !continuingAfterWin;

  // Check if player has found every available word
  const allWordsFound = hints?.num_words > 0 && submittedWords.length >= hints.num_words;
  const showAllWordsFoundState = allWordsFound && continuingAfterWin;

  // Board is disabled in any end state (initial win or all words found)
  const boardDisabled = showWinState || showAllWordsFoundState;

  // Initialize shuffled letters when config loads
  useEffect(() => {
    if (letters.length > 0 && shuffledLetters.length === 0) {
      setShuffledLetters([...letters]);
    }
  }, [letters, shuffledLetters.length]);

  // Handle difficulty update (defined early for use in showDifficultyModal)
  const updateDifficulty = useCallback(
    (level) => {
      setPendingDifficulty(level);
      if (shouldTrack) {
        logGameEvent("difficulty_selected", gameConfig.id, effectiveDate, { difficulty: level });
      }
    },
    [gameConfig.id, effectiveDate, shouldTrack]
  );

  // Confirm difficulty (defined early for use in showDifficultyModal)
  const confirmDifficulty = useCallback(async () => {
    // Send difficulty to server
    const result = await setDifficulty(pendingDifficulty);
    if (result.success) {
      setShowDifficultySelection(false);
      closeModal(); // Close the difficulty modal
    } else {
      snackAlertRef.current?.showSnackAlert("Errore nell'impostazione della difficoltà", "error");
    }
  }, [pendingDifficulty, setDifficulty, closeModal]);

  // Function to show difficulty selection modal
  const showDifficultyModal = useCallback(() => {
    setIsDifficultyModalOpen(true);
    showModal({
      title: "Scegli la difficoltà",
      content: (
        <DifficultySelectionContent
          pendingDifficulty={pendingDifficulty}
          onDifficultyChange={updateDifficulty}
          onConfirm={confirmDifficulty}
          isEventPending={isEventPending}
          getDifficultyReward={getOrtografiaDifficultyReward}
        />
      ),
    });
  }, [showModal, pendingDifficulty, isEventPending, updateDifficulty, confirmDifficulty, getOrtografiaDifficultyReward]);

  // Show difficulty selection for new games (only on initial load)
  useEffect(() => {
    if (!isLoading && sessionState && !hasCheckedInitialDifficultyRef.current) {
      hasCheckedInitialDifficultyRef.current = true;

      const needsDifficultySelection = status === GameStatus.NOT_STARTED && score === 0 && seq === 0;
      const isFirstTimePlayer = !hasGameHistory(gameConfig.id);

      if (isPreview) {
        if (needsDifficultySelection) {
          showDifficultyModal();
        }
        return;
      }

      // Show instructions for first-time players first
      if (isFirstTimePlayer && !hasShownInstructionsRef.current) {
        hasShownInstructionsRef.current = true;
        // If we also need difficulty selection, queue it to show after instructions close
        if (needsDifficultySelection) {
          setPendingDifficultyModal(true);
        }
        showModal({
          title: "Istruzioni",
          content: <Instructions />,
        });
        if (shouldTrack) {
          logGameEvent("instructions_viewed", gameConfig.id, effectiveDate, { first_time: true });
        }
      } else if (needsDifficultySelection) {
        // Show difficulty selection modal directly
        showDifficultyModal();
      }
    }
  }, [
    isLoading,
    sessionState,
    status,
    score,
    seq,
    gameConfig.id,
    showModal,
    showDifficultyModal,
    isPreview,
    shouldTrack,
    effectiveDate,
  ]);

  // Show difficulty selection after instructions modal closes
  useEffect(() => {
    if (!isModalOpen && pendingDifficultyModal) {
      setPendingDifficultyModal(false);
      // Small delay to let the close animation complete
      const timer = setTimeout(() => {
        showDifficultyModal();
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isModalOpen, pendingDifficultyModal, showDifficultyModal]);

  // Calculate level index and check for milestones
  useEffect(() => {
    if (!levels || Object.keys(levels).length === 0) return;

    // Use C2 difficulty for progress calculations only if player continues after winning
    const effectiveDifficulty = continuingAfterWin ? "C2" : difficulty;
    const levelEntries = Object.entries(levels)
      .filter(([level]) => level <= effectiveDifficulty)
      .sort((x, y) => x[1] - y[1]);

    let newLevelIndex = 0;
    for (let i = 0; i < levelEntries.length; i++) {
      if (score >= levelEntries[i][1]) {
        newLevelIndex = i;
      }
    }

    // Check if we crossed a milestone (level increased and score changed)
    if (newLevelIndex > currentLevelIndex && score > prevScoreRef.current) {
      if (shouldTrack) {
        logGameEvent("level_completed", gameConfig.id, effectiveDate, {
          level_detail: levelEntries[newLevelIndex][0],
          game_progress: (newLevelIndex + 1) / levelEntries.length,
        });
      }

      // Trigger the inline animation for all level-ups (including winning)
      setPreviousLevelIndex(currentLevelIndex);
      setLevelUpAnimation(true);
      // Auto-clear animation after it completes
      setTimeout(() => setLevelUpAnimation(false), 1500);
    }

    setCurrentLevelIndex(newLevelIndex);
    prevScoreRef.current = score;
  }, [score, levels, difficulty, currentLevelIndex, gameConfig.id, effectiveDate, shouldTrack, continuingAfterWin]);

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      const { key, ctrlKey, metaKey, altKey } = event;
      if (ctrlKey || metaKey || altKey) return;

      if (showDifficultySelection || boardDisabled) return;

      if (/^[a-zA-Z]$/.test(key)) {
        const letter = key.toUpperCase();
        if ([...shuffledLetters, centerLetter].includes(letter)) {
          addLetter(letter);
        }
      } else if (key === "Backspace") {
        deleteLetter();
      } else if (key === "Enter") {
        event.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentWord, shuffledLetters, centerLetter, showDifficultySelection, boardDisabled]);

  // Toggle dropdown state
  const toggleDropdown = () => {
    setIsExpanded((prev) => !prev);
  };

  // Shuffle letters
  const shuffleLetters = () => {
    const shuffled = [...letters].sort(() => Math.random() - 0.5);
    setShuffledLetters(shuffled);
  };

  // Add a letter to the current word
  const addLetter = (letter) => {
    setCurrentWord((prev) => {
      if (scaffold && prev.length >= scaffold.wordLength) {
        return prev;
      }
      return prev + letter;
    });
  };

  // Delete the last letter
  const deleteLetter = () => {
    setCurrentWord((prev) => prev.slice(0, -1));
  };

  // Handle submission
  const handleSubmit = async () => {
    let wordToSubmit = currentWord;

    // If scaffold is active, construct full word
    if (scaffold) {
      const fullWord = [];
      for (let i = 0; i < scaffold.wordLength; i++) {
        const userLetter = currentWord[i]?.toUpperCase() || "";
        const scaffoldLetter = scaffold.revealedLetters[i] || "";
        fullWord.push(userLetter || scaffoldLetter);
      }
      wordToSubmit = fullWord.join("");

      if (wordToSubmit.length < scaffold.wordLength) {
        snackAlertRef.current?.showSnackAlert("Completa la parola", "error");
        return;
      }
    }

    const isCenterUsed = wordToSubmit.toUpperCase().includes(centerLetter);
    const isAlreadySubmitted = submittedWords.map((x) => removeAccents(x.toLowerCase())).includes(wordToSubmit.toLowerCase());
    const isTooShort = wordToSubmit.length < 4;

    // Front end validation
    if (isTooShort) {
      setCurrentWord("");
      snackAlertRef.current?.showSnackAlert("Troppo corta", "error");
      return;
    } else if (isAlreadySubmitted) {
      setCurrentWord("");
      snackAlertRef.current?.showSnackAlert("Già trovata", "error");
      return;
    } else if (!isCenterUsed) {
      setCurrentWord("");
      snackAlertRef.current?.showSnackAlert("Usa la lettera al centro", "error");
      return;
    }

    // Back end validation via event
    setIsSubmitting(true);
    try {
      const response = await submitWord(wordToSubmit.toLowerCase());

      if (!response.success) {
        const errorMessage = response.message || "Errore durante l'invio";
        setCurrentWord("");
        snackAlertRef.current?.showSnackAlert(errorMessage, "error");
        return;
      }

      const submitResult = response.result || {};
      if (submitResult.valid === false) {
        const errorMessage = getOrtografiaInvalidReasonMessage(submitResult.reason);

        if (shouldTrack) {
          logGameEvent("guess_incorrect", gameConfig.id, effectiveDate, { word: wordToSubmit });
        }
        setCurrentWord("");
        snackAlertRef.current?.showSnackAlert(errorMessage, "error");
      } else if (submitResult.valid) {
        // Valid word
        if (shouldTrack) {
          logGameEvent("guess_correct", gameConfig.id, effectiveDate, { word: wordToSubmit });
        }

        const points = submitResult.points;
        const message = points > 15 ? "Ottimo" : points > 10 ? "Complimenti" : "Ben fatto";
        snackAlertRef.current?.showSnackAlert(`${message}! +${points}`, "success");
        // Note: scaffold is cleared by the backend when a word is submitted successfully
      } else {
        snackAlertRef.current?.showSnackAlert("Errore durante l'invio", "error");
      }
      setCurrentWord("");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect to auth page for unauthenticated users
  const redirectToAuth = useCallback((allowanceOverride = null) => {
    if (isPreview) return;
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: (hasEverAuthenticated || hintAllowance?.eligible === false) ? 'sign_in' : 'sign_up',
      source_surface: "game_hints",
      gate_reason: "hints_requires_auth",
      next_path: `${location.pathname}${location.search || ''}${location.hash || ''}`,
      message: getHintAuthMessage(allowanceOverride?.game_slug === gameConfig.id ? allowanceOverride : hintAllowance),
      is_returning_device: hasEverAuthenticated,
      game_slug: gameConfig?.id || null,
      track_gate_viewed: true,
      trigger: "hint_click_redirect",
    });
    navigate(authUrl);
  }, [gameConfig?.id, hasEverAuthenticated, location.pathname, location.search, navigate, isPreview, hintAllowance, location.hash]);

  // Scaffold hint handler
  const hintCosts = useMemo(
    () => ({
      scaffold: getHintCost("ortografia", "scaffold"),
    }),
    [getHintCost]
  );

  const hintAffordability = useMemo(
    () => ({
      scaffold: canAffordHintCost({ cost: hintCosts.scaffold, balance, isPreview, isFree: hasFreeHints }),
    }),
    [balance, hintCosts.scaffold, isPreview, hasFreeHints]
  );

  const handleScaffold = useCallback(async () => {
    if (boardDisabled) {
      return;
    }
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!hintAffordability.scaffold) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_scaffold',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.scaffold,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }

    setAiutiniError("");
    setLoadingHint("scaffold");
    try {
      const result = await requestScaffold(redirectToAuth);

      if (result.success) {
        // Reset dismissed flag so the new scaffold shows
        setScaffoldDismissed(false);
        setCurrentWord("");
        snackAlertRef.current?.showSnackAlert("Aiutino usato!", "success", 2000);
        closeModal();
      } else if (!result.success) {
        if (result.error === "INSUFFICIENT_BALANCE") {
          setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
        } else if (result.error === "NO_WORDS_LEFT") {
          setAiutiniError("Hai gia trovato tutte le parole!");
        } else if (result.error === "NOT_AUTHENTICATED") {
          return;
        } else {
          setAiutiniError(result.message || "Errore durante la richiesta");
        }
      }
    } catch (err) {
      console.error(err);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  }, [balance, boardDisabled, closeModal, effectiveDate, gameConfig.id, hintAffordability.scaffold, hintCosts.scaffold, isSignedIn, hasFreeHints, redirectToAuth, requestScaffold, shouldTrack, trackGameHintInsufficientBalance, isPreview]);
  handleScaffoldRef.current = handleScaffold;

  // Cancel scaffold (dismiss locally - will reappear on reload if still in session)
  const cancelScaffold = () => {
    setScaffoldDismissed(true);
    setCurrentWord("");
  };

  // Calculate level progress - only expand to C2 when continuing after win
  const effectiveDifficulty = continuingAfterWin ? "C2" : difficulty;
  const levelEntries = useMemo(() => {
    return Object.entries(levels)
      .filter(([level]) => level <= effectiveDifficulty)
      .sort((x, y) => x[1] - y[1]);
  }, [levels, effectiveDifficulty]);

  let pointsToNextLevel = 0;
  let nextLevel;
  if (levelEntries.length) {
    const nextLevelIndex = Math.min(currentLevelIndex + 1, levelEntries.length - 1);
    nextLevel = levelEntries[nextLevelIndex];
    pointsToNextLevel = nextLevel[1] - score;
  }

  // Update Aiutini modal content when loadingHint changes
  // Using ref for handler to avoid infinite update loops
  useEffect(() => {
    if (!isAiutiniModalOpen) {
      aiutiniModalSignatureRef.current = "";
      setAiutiniError("");
      return;
    }

    const nextSignature = JSON.stringify({
      loadingHint: loadingHint || "",
      aiutiniError,
      scaffoldUsed: !!hints.scaffold,
      disabled: !!boardDisabled,
      scaffoldCost: hintCosts.scaffold ?? null,
      scaffoldAffordable: !!hintAffordability.scaffold,
      isSignedIn,
      hintAllowance,
      hasFreeHints,
      hasEverAuthenticated,
    });

    if (aiutiniModalSignatureRef.current === nextSignature) {
      return;
    }
    aiutiniModalSignatureRef.current = nextSignature;

    refreshAiutiniModal(
      <Aiutini
        onScaffold={() => handleScaffoldRef.current?.()}
        disabled={boardDisabled}
        hintCosts={hintCosts}
        hintAffordability={hintAffordability}
        hints={hints}
        loadingHint={loadingHint}
        errorMessage={aiutiniError}
        isSignedIn={isSignedIn}
        hasFreeHints={hasFreeHints}
        isPreview={isPreview}
        onAuthRedirect={redirectToAuth}
        hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
        balance={balance}
      />,
      "Aiutini",
    );
  }, [aiutiniError, boardDisabled, hasEverAuthenticated, hintAffordability, hints, hintCosts, isAiutiniModalOpen, isSignedIn, hasFreeHints, loadingHint, redirectToAuth, refreshAiutiniModal, isPreview, hintAllowance]);

  // Update Difficulty modal content when pendingDifficulty or isEventPending changes
  useEffect(() => {
    if (isDifficultyModalOpen) {
      showModal({
        title: "Scegli la difficoltà",
        content: (
          <DifficultySelectionContent
            pendingDifficulty={pendingDifficulty}
            onDifficultyChange={updateDifficulty}
            onConfirm={confirmDifficulty}
            isEventPending={isEventPending}
            getDifficultyReward={getOrtografiaDifficultyReward}
          />
        ),
      });
    }
  }, [isDifficultyModalOpen, pendingDifficulty, isEventPending, showModal, updateDifficulty, confirmDifficulty, getOrtografiaDifficultyReward]);

  const handleClosePreview = useCallback(() => {
    window.close();
  }, []);

  const isGameplayAdActive = !boardDisabled && !showDifficultySelection;
  const mobileGameplayAd = useMemo(() => ({
    enabled: isGameplayAdActive || !!endSectionAnchor,
    anchors: endSectionAnchor ? [endSectionAnchor] : [gameplayControlsRef],
    placement: `game-shell-${gameConfig.id}-mobile-gameplay-banner`,
    testId: `${gameConfig.id}-mobile-gameplay-banner`,
  }), [endSectionAnchor, gameConfig.id, isGameplayAdActive]);

  // Build header actions
  const headerActions = useMemo(() => {
    if (isLoading) return [];

    const actions = [
      {
        icon: "fa fa-question-circle",
        title: "Istruzioni",
        onClick: () => {
          if (shouldTrack) {
            logGameEvent("instructions_viewed", gameConfig.id, effectiveDate, { first_time: false });
          }
          showModal({
            title: "Istruzioni",
            content: <Instructions />,
          });
        },
      },
      {
        icon: "fa fa-medkit",
        title: "Aiutini",
        onClick: () => {
          setAiutiniError("");
          if (shouldTrack) {
            logGameEvent("help_viewed", gameConfig.id, effectiveDate);
          }
          showAiutiniModal(
            <Aiutini
              onScaffold={() => handleScaffoldRef.current?.()}
              disabled={boardDisabled}
              hintCosts={hintCosts}
              hintAffordability={hintAffordability}
              hints={hints}
              loadingHint={loadingHint}
              errorMessage=""
              isSignedIn={isSignedIn}
              hasFreeHints={hasFreeHints}
              onAuthRedirect={redirectToAuth}
              hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
              balance={balance}
              isPreview={isPreview}
            />,
            "Aiutini",
          );
        },
      },
    ];

    const showSolutionsModal = (nextSolutions) => {
      if (shouldTrack) {
        logGameEvent("answers_viewed", gameConfig.id, effectiveDate);
      }
      showModal({
        title: "Soluzioni",
        content: <Answers solutions={nextSolutions || []} submittedWords={submittedWords} />,
      });
    };

    const loadAndShowSolutions = async () => {
      const result = await viewSolutions();
      if (!result.success) {
        snackAlertRef.current?.showSnackAlert(result.message || "Errore nel caricamento delle soluzioni", "error");
        return;
      }
      showSolutionsModal(result.result?.solutions || []);
    };

    if (hasSolutions || solutionsAvailable) {
      actions.push({
        icon: "fa fa-check-circle",
        title: "Soluzioni",
        onClick: async () => {
          if (hasSolutions) {
            showSolutionsModal(solutions || []);
            return;
          }

          if (willViewingSolutionsForfeitGame) {
            showModal({
              title: "Soluzioni",
              content: (
                <SolutionsWarning
                  onCancel={closeModal}
                  onConfirm={loadAndShowSolutions}
                />
              ),
            });
            return;
          }

          await loadAndShowSolutions();
        },
      });
    }

    return actions;
  }, [
    isLoading,
    effectiveDate,
    boardDisabled,
    hints,
    hintCosts,
    loadingHint,
    gameConfig.id,
    showModal,
    submittedWords,
    solutions,
    hasSolutions,
    solutionsAvailable,
    willViewingSolutionsForfeitGame,
    isSignedIn,
    redirectToAuth,
    hasEverAuthenticated,
    isPreview,
    showAiutiniModal,
    shouldTrack,
    viewSolutions,
    closeModal,
    hasFreeHints,
  ]);

  const num_rows = Math.ceil(submittedWords.length / 2);
  const gridStyle = {
    gridTemplateRows: `repeat(${num_rows}, auto)`,
  };

  // Show skeleton while loading
  if (isLoading || !puzzleConfig) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
      >
        <div className={styles.ortografiaContainer}>
          <p className={gameStyles.greeting}>{getGameMessage(gameConfig.id, "not_started")}</p>
          <OrtografiaSkeleton />
        </div>
      </GameShell>
    );
  }

  // Show error state
  if (sessionError) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
      >
        <div className={styles.ortografiaContainer}>
          <p className={gameStyles.greeting}>Errore nel caricamento del gioco</p>
        </div>
      </GameShell>
    );
  }

  return (
    <GameShell
      gameConfig={gameConfig}
      actions={headerActions}
      sponsorship={sponsorship}
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      modalTitle={modalTitle}
      modalContent={modalContent}
      key={sessionState?.session_id || effectiveDate}
      resultsPresentationReady={!levelUpAnimation}
      showCelebration={justWon}
      isCompleted={boardDisabled}
      showNavigation={showNavigation}
      showGameHeader={showGameHeader}
      mobileGameplayAd={mobileGameplayAd}
    >
      <SnackAlert ref={snackAlertRef} />
      <div className={styles.ortografiaContainer}>
        <p className={gameStyles.greeting}>{getGameMessage(gameConfig.id, "not_started")}</p>

        {/* Main game board - show if difficulty is set (not pending initial selection) */}
        {!showDifficultySelection && (
          <>
            <div className={styles.scoreBar}>
              <div className={`${styles.levelLabel} ${levelUpAnimation ? styles.levelLabelPulse : ""}`}>
                {levelEntries[currentLevelIndex]?.[0]}
              </div>
              <div className={styles.progressBar}>
                {/* Permanent fill showing progress up to current level */}
                {currentLevelIndex > 0 && (
                  <div
                    className={styles.progressFillPermanent}
                    style={{
                      width: `${(currentLevelIndex / (levelEntries.length - 1)) * 100}%`,
                    }}
                  />
                )}
                {/* Animated fill overlay for level-up animation */}
                {levelUpAnimation && (
                  <div
                    className={styles.progressFillAnim}
                    style={{
                      "--fill-start": `${(previousLevelIndex / (levelEntries.length - 1)) * 100}%`,
                      "--fill-end": `${(currentLevelIndex / (levelEntries.length - 1)) * 100}%`,
                    }}
                  />
                )}
                {levelEntries.map(([level], index) => (
                  <div
                    key={level}
                    className={`${styles.progressDot} ${index === currentLevelIndex ? styles.active : ""} ${index < currentLevelIndex ? styles.done : ""} ${index === currentLevelIndex + 1 ? styles.next : ""} ${levelUpAnimation && index === currentLevelIndex ? styles.levelUpRings : ""}`}
                  >
                    {index === currentLevelIndex && <span className={styles.score}>{score}</span>}
                    {index === currentLevelIndex + 1 && <span className={styles.score}>{pointsToNextLevel}</span>}
                  </div>
                ))}
              </div>
            </div>
            {/* <AIHelperTrigger puzzleInstanceId={puzzleInstanceId} gameSlug={gameConfig.id}> */}
            <div className={`${styles.guessedWords} ${isExpanded ? styles.expanded : ""}`}>
              {!isExpanded && (
                <div className={styles.wordsPreview}>
                  {submittedWords.length === 0 ? (
                    <span className={styles.introText}>Le tue parole appariranno qui</span>
                  ) : (
                    submittedWords
                      .slice() // make a shallow copy to not mutate original
                      .reverse()
                      .map((word, idx) => (
                        <span key={idx} className={styles.guessedWord} onClick={() => handleWordClick(word)}>
                          {word}
                        </span>
                      ))
                  )}
                </div>
              )}
              <IconButton
                iconClassName={isExpanded ? "fas fa-chevron-up" : "fas fa-chevron-down"}
                onClick={toggleDropdown}
                className={styles.chevronButton}
              />
              {isExpanded && (
                <div className={styles.wordsFullView}>
                  <div className={styles.summary}>
                    {`Hai trovato ${submittedWords.length} ${submittedWords.length > 1 ? "parole" : "parola"}`}
                  </div>
                  <div className={styles.foundWords} style={gridStyle}>
                    {submittedWords.slice().sort().map((word, index) => (
                      <div key={index} className={styles.wordColumn}>
                        <span onClick={() => handleWordClick(word)}>{word}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* </AIHelperTrigger> */}
            {/* Word display / scaffold area - hidden when board is disabled */}
            {!boardDisabled && (
              <div className={styles.guessFeedbackContainer}>
                {scaffold ? (
                  <div className={styles.scaffoldContainer}>
                    <div className={styles.scaffoldWord}>
                      {Array.from({ length: scaffold.wordLength }).map((_, idx) => {
                        const userLetter = currentWord[idx]?.toUpperCase() || "";
                        const scaffoldLetter = scaffold.revealedLetters[idx] || "";
                        const displayLetter = userLetter || scaffoldLetter;
                        const isHintLetter = !userLetter && scaffoldLetter;

                        return (
                          <span key={idx} className={`${styles.scaffoldChar} ${isHintLetter ? styles.scaffoldHint : ""}`}>
                            {displayLetter}
                          </span>
                        );
                      })}
                      <button className={styles.scaffoldCancel} onClick={cancelScaffold} title="Annulla suggerimento">
                        <FaIcon className="fas fa-times"  />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.wordDisplay}>{currentWord}</div>
                )}
              </div>
            )}

            {/* Hexagon grid with optional overlays */}
            <div className={`${styles.hexagonGridWrapper} ${boardDisabled ? styles.hexagonGridSpaced : ""}`}>
              <div className={`${styles.hexagonGrid} ${boardDisabled ? styles.hexagonGridDisabled : ""}`}>
                <div className={styles.row}>
                  <div className={`${styles.hexagon} ${boardDisabled ? styles.hexagonDisabled : ""}`} onClick={boardDisabled ? undefined : () => addLetter(shuffledLetters[0])}>
                    {shuffledLetters[0]}
                  </div>
                  <div className={`${styles.hexagon} ${boardDisabled ? styles.hexagonDisabled : ""}`} onClick={boardDisabled ? undefined : () => addLetter(shuffledLetters[1])}>
                    {shuffledLetters[1]}
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={`${styles.hexagon} ${boardDisabled ? styles.hexagonDisabled : ""}`} onClick={boardDisabled ? undefined : () => addLetter(shuffledLetters[2])}>
                    {shuffledLetters[2]}
                  </div>
                  <div className={`${styles.hexagon} ${styles.centerHexagon} ${boardDisabled ? styles.hexagonDisabled : ""}`} onClick={boardDisabled ? undefined : () => addLetter(centerLetter)}>
                    {centerLetter}
                  </div>
                  <div className={`${styles.hexagon} ${boardDisabled ? styles.hexagonDisabled : ""}`} onClick={boardDisabled ? undefined : () => addLetter(shuffledLetters[3])}>
                    {shuffledLetters[3]}
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={`${styles.hexagon} ${boardDisabled ? styles.hexagonDisabled : ""}`} onClick={boardDisabled ? undefined : () => addLetter(shuffledLetters[4])}>
                    {shuffledLetters[4]}
                  </div>
                  <div className={`${styles.hexagon} ${boardDisabled ? styles.hexagonDisabled : ""}`} onClick={boardDisabled ? undefined : () => addLetter(shuffledLetters[5])}>
                    {shuffledLetters[5]}
                  </div>
                </div>
              </div>

              {/* Continue button overlay - shown on top of disabled grid */}
              {showWinState && (
                <div className={styles.continueOverlay}>
                  <Button secondary onClick={() => setContinuingAfterWin(true)}>
                    Continua
                  </Button>
                </div>
              )}

              {/* All words found overlay */}
              {showAllWordsFoundState && (
                <div className={styles.continueOverlay}>
                  <span className={styles.allWordsFoundMessage}>Tutte le parole trovate!</span>
                </div>
              )}
            </div>

            {/* Button row - hidden when board is disabled */}
            {!boardDisabled && (
              <div className={styles.buttonRow} ref={gameplayControlsRef}>
                <Button onClick={deleteLetter} disabled={currentWord.length === 0}>
                  Cancella
                </Button>
                <IconButton iconClassName="fas fa-sync-alt" className={styles.shuffleButton} onClick={shuffleLetters} />
                <Button
                  primary={currentWord.length >= 4}
                  onClick={handleSubmit}
                  disabled={isSubmitPending}
                  loading={isSubmitPending}
                  loadingText="Invio"
                >
                  Invia
                </Button>
              </div>
            )}

            {/* Keep the results controller mounted throughout continued play. */}
            {status === GameStatus.WON && (
              <div className={styles.gameEndContainer}>
                {isPreview && !previewSponsorship ? (
                  <div ref={setEndSectionAnchor} className={styles.previewEndSection}>
                    <Button primary onClick={handleClosePreview}>
                      {previewEndLabel || "Chiudi anteprima"}
                    </Button>
                  </div>
                ) : (
                  <GameEndSection
                    ref={setEndSectionAnchor}
                    won={true}
                    justWon={justWon}
                    gameConfig={gameConfig}
                    sessionState={sessionState}
                    puzzleConfig={puzzleConfig}
                    justCompleted={justWon || justLost}
                    date={effectiveDate}
                    shouldTrack={shouldTrack}
                    sponsorship={sponsorship}
                    previewMode={isPreview}
                    message={`Hai raggiunto il livello ${levelEntries[currentLevelIndex]?.[0]}!`}
                    snackAlertRef={snackAlertRef}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </GameShell>
  );
};

export default OrtografiaGame;

const AnimatedHive = () => {
  const [step, setStep] = useState(0);
  const demoWord = "SPIAGGIA";

  useEffect(() => {
    const timer = setInterval(() => {
      setStep((prev) => (prev < demoWord.length ? prev + 1 : 0));
    }, 800);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="mini-hive-demo">
      <div className="word-display">{demoWord.slice(0, step)}</div>
      <div className="mini-hexagon-grid">
        <div className="row">
          <div className={`hexagon ${demoWord[step] === "S" ? "highlight" : ""}`}>S</div>
          <div className={`hexagon ${demoWord[step] === "P" ? "highlight" : ""}`}>P</div>
        </div>
        <div className="row">
          <div className={`hexagon ${demoWord[step] === "I" ? "highlight" : ""}`}>I</div>
          <div className={`hexagon center ${demoWord[step] === "A" ? "highlight" : ""}`}>A</div>
          <div className={`hexagon ${demoWord[step] === "G" ? "highlight" : ""}`}>G</div>
        </div>
        <div className="row">
          <div className={`hexagon ${demoWord[step] === "E" ? "highlight" : ""}`}>E</div>
          <div className={`hexagon ${demoWord[step] === "R" ? "highlight" : ""}`}>R</div>
        </div>
      </div>
    </div>
  );
};

const Instructions = () => {
  return (
    <div className={`${modalStyles.modalContent} ${modalStyles.instructions}`}>
      <p>Usa le sette lettere per creare parole italiane. Ecco le regole:</p>
      <ul>
        <li>Le parole devono avere almeno 4 lettere</li>
        <li>Devi usare sempre la lettera al centro (quella gialla)</li>
        <li>Puoi usare la stessa lettera più volte</li>
        <li>La lista non include parole con trattini, nomi propri, abbreviazioni o parole dialettali</li>
      </ul>
      <AnimatedHive />
      <p>Più parole trovi, più punti guadagni! Prova a raggiungere il tuo livello!</p>
      <h3>Punteggi</h3>
      <p>Ogni parola ha un punteggio diverso, in base al suo livello e alla sua lunghezza, come segue:</p>
      <ul>
        <li>Punti per livello: 0 (A1), 2 (A2), 4 (B1), 10 (B2), 15 (C1), 20 (C2);</li>
        <li>Punti per lunghezza: ricevi 1 punto per ogni lettera nella parola.</li>
      </ul>
      <p>Se trovi un pangramma, una parola che utilizza tutte e sette le lettere, ottiene un bonus di 25 punti.</p>
      <p>Ad esempio, la parola "convoluto" vale 44 punti: 10 (B2) + 9 (numero di lettere) + 25 (pangramma).</p>
    </div>
  );
};

const Aiutini = ({
  onScaffold,
  disabled = false,
  hintCosts = {},
  hintAffordability = {},
  hints,
  loadingHint = null,
  errorMessage = "",
  isSignedIn = true,
  hasFreeHints = false,
  onAuthRedirect = null,
  hasEverAuthenticated = false,
  isPreview = false,
  balance = 0,
}) => {
  // Helper to render button content with cost
  const renderButtonContent = (cost) => {
    if (isPreview) {
      return "Ricevi aiutino";
    }
    if (!isSignedIn && !hasFreeHints) {
      return hasEverAuthenticated ? 'Accedi' : 'Registrati';
    }
    return "Usa aiutino";
  };
  const shouldShowCostChip = (cost) => !isPreview && (hasFreeHints || (Number.isFinite(Number(cost)) && Number(cost) > 0));

  // Suggerimenti grid logic
  const { num_pangrams, word_lengths_by_letter, num_words } = hints || {};

  const getLastNonZeroIndex = (array) => {
    for (let i = array.length - 1; i >= 0; i--) {
      if (array[i] > 0) {
        return i;
      }
    }
    return -1;
  };

  const maxWordLength = word_lengths_by_letter
    ? 3 +
      Math.max(
        ...Object.values(word_lengths_by_letter).map((counts) => getLastNonZeroIndex(counts) + 1)
      )
    : 0;

  const letters = word_lengths_by_letter ? Object.keys(word_lengths_by_letter).sort() : [];
  const wordLengths = Array.from({ length: maxWordLength - 3 }, (_, i) => i + 4);
  const disabledHintCount = getDisabledHintCount({
    isSignedIn,
    isPreview,
    hintAffordability,
  });
  const shouldShowInsufficientBalanceState = isSignedIn && (
    errorMessage === INSUFFICIENT_HINT_BALANCE_MESSAGE
    || disabledHintCount > 0
  );
  const shouldShowGenericError = Boolean(errorMessage) && !shouldShowInsufficientBalanceState;

  return (
    <div className={`${modalStyles.modalContent} ${modalStyles.instructions}`}>
      {shouldShowInsufficientBalanceState && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <div className={styles.aiutiniInlineErrorContent}>
            <div>
              {getDisabledHintsMessage({
                balance,
                disabledHintCount: Math.max(disabledHintCount, 1),
              })}
            </div>
            <div className={styles.aiutiniInlineErrorCard}>
              <RewardedGettonciniCard
                sourceSurface="aiutini_modal"
                gameSlug="ortografia"
                compact
              />
            </div>
          </div>
        </div>
      )}
      {shouldShowGenericError && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <span>
            {errorMessage}
          </span>
        </div>
      )}
      {/* Scaffold hint */}
      {(!isSignedIn || !shouldShowInsufficientBalanceState) && (
        <p className={styles.aiutinoPreamble}>
          {!isSignedIn && !isPreview && !hasFreeHints ? (
            <>
              Per usare gli aiutini hai bisogno di gettoncini, che puoi guadagnare con un account gratuito.{" "}
              <span className={styles.authLink} onClick={onAuthRedirect}>
                {hasEverAuthenticated ? "Accedi" : "Registrati"} <FaIcon className="fa-solid fa-arrow-right"  />
              </span>
            </>
          ) : "Se sei bloccato, puoi usare questo aiutino e la tabella qui sotto."}
        </p>
      )}
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}>
              <FaIcon className="fa-solid fa-spell-check"  />
            </span>
            <span className={styles.aiutinoTitle}>Suggerisci parola</span>
            <span className={styles.reusableBadge}>
              <FaIcon className="fa-solid fa-rotate"  />
            </span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              onClick={onScaffold}
              className={[
                styles.aiutinoActionRight,
                isSignedIn && !hintAffordability.scaffold ? styles.aiutinoUnavailable : "",
              ].filter(Boolean).join(" ")}
              disabled={disabled || loadingHint !== null || (isSignedIn && !hintAffordability.scaffold)}
              loading={loadingHint === "scaffold"}
              aria-disabled={disabled || (isSignedIn && !hintAffordability.scaffold)}
            >
              {renderButtonContent(hintCosts.scaffold)}
            </Button>
            {shouldShowCostChip(hintCosts.scaffold) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.scaffold} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Mostra uno schema con alcune lettere riempite.</span>
      </div>

      {/* Suggerimenti table */}
      {hints && (
        <>
          <p style={{ marginTop: "16px" }}>
            Ci sono <b>{`${num_words} parole`}</b> da trovare, inclusi{" "}
            <b>{`${num_pangrams} ${num_pangrams > 1 ? "pangramme" : "pangramma"}`}</b>.
          </p>
          <p>La tabella seguente mostra:</p>
          <ul>
            <li>a sinistra, la prima lettera di ogni parola;</li>
            <li>in alto, il numero di lettere di ogni parola;</li>
            <li>
              all'incrocio dei due parametri, il numero di parole che iniziano con una data lettera e hanno
              un dato numero di lettere.
            </li>
          </ul>
          {/* Grid header */}
          <div className={styles.gridHeader}>
            <div className={[styles.gridCell, styles.headerCell].join(" ")}></div>
            {wordLengths.map((length) => (
              <div key={length} className={[styles.gridCell, styles.headerCell].join(" ")}>
                {length}
              </div>
            ))}
          </div>

          <div className={styles.hintsGrid}>
            {/* Grid rows */}
            {letters.map((letter) => (
              <div key={letter} className={styles.gridRow}>
                {/* Row header */}
                <div className={[styles.gridCell, styles.rowHeader].join(" ")}>{letter.toUpperCase()}</div>
                {/* Row content */}
                {wordLengths.map((length) => {
                  const count = word_lengths_by_letter[letter][length - 4] || 0;
                  return (
                    <div
                      key={`${letter}-${length}`}
                      className={[styles.gridCell, count === 0 && styles.zeroCell].filter(Boolean).join(" ")}
                    >
                      {count === 0 ? "-" : count}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}
      <div className={styles.reusableHint}>
        <FaIcon className="fa-solid fa-rotate"  />
        <span>Può essere riutilizzato più volte.</span>
      </div>
    </div>
  );
};

const Answers = ({ solutions, submittedWords }) => {
  const answers = (solutions || [])
    .map((item) => item.word)
    .filter(Boolean);

  const num_rows = Math.ceil(answers.length / 2);
  const gridStyle = {
    gridTemplateRows: `repeat(${num_rows}, auto)`,
  };

  return (
    <div className={modalStyles.modalContent}>
      <div className={styles.wordsFullView}>
        <div className={styles.foundWords} style={gridStyle}>
          {answers.slice().sort().map((word, index) => {
            const isFound = submittedWords.includes(word);
            return (
              <div key={index} className={styles.wordColumn}>
                <FaIcon
                  className={`fas fa-check`}
                  style={{
                    color: isFound ? "#ffe019" : "transparent",
                    marginRight: "8px",
                    display: "inline-block",
                    width: "12px",
                  }}
                 />
                <span style={{ fontWeight: isFound ? "bold" : "normal" }}>{word}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SolutionsWarning = ({ onCancel, onConfirm }) => {
  return (
    <div className={modalStyles.modalContent}>
      <div className={styles.solutionsWarning}>
        <p className={styles.solutionsWarningBody}>
          Se apri le soluzioni adesso, la partita finisce subito e conta come persa. Puoi continuare a inserire parole, ma non puoi ricevere premi.
        </p>
        <div className={styles.solutionsWarningActions}>
          <Button secondary onClick={onCancel}>
            Torna a giocare
          </Button>
          <Button primary onClick={onConfirm}>
            Vedi le soluzioni
          </Button>
        </div>
      </div>
    </div>
  );
};
